"use client";

import React from "react";
import Reveal from "@/components/common/Reveal";
import { Layers, ShieldCheck, ThermometerSnowflake, Funnel as Filter, ArrowRight, Cpu, FileSpreadsheet } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface TechnicalPillar {
  icon: React.ComponentType<{
    className?: string;
  }>;
  title: Record<Lang, string>;
  summary: Record<Lang, string>;
  detail: Record<Lang, string>;
}
interface LayerConfig {
  title: Record<Lang, string>;
  tag: Record<Lang, string>;
  desc: Record<Lang, string>;
  specs: Record<Lang, string>;
  useCase: Record<Lang, string>;
}
interface ComparisonMetric {
  property: Record<Lang, string>;
  sintered: Record<Lang, string>;
  unbonded: Record<Lang, string>;
}
const copyData = {
  sectionBadge: {
    en: "Technical Resources",
    zh: "技术资源",
    es: "Recursos técnicos",
    de: "Technische Ressourcen",
    fr: "Ressources techniques",
    pt: "Recursos técnicos"
  },
  heading: {
    en: "Sintered mesh engineering guide",
    zh: "烧结网工程技术指南",
    es: "Guía de ingeniería de malla sinterizada",
    de: "Leitfaden für Sintermetallgewebe",
    fr: "Guide d'ingénierie des toiles frittées",
    pt: "Guia de engenharia de malha sinterizada"
  },
  lead: {
    en: "Understand layer configurations, pore stability, and thermal limits before specifying diffusion-bonded filter media for demanding industrial processes.",
    zh: "在为严苛工业工艺选型扩散焊接过滤介质前，深入了解层叠结构配置、孔隙稳定性与耐温极限。",
    es: "Comprenda las configuraciones de capas, la estabilidad de los poros y los límites térmicos antes de especificar medios filtrantes sinterizados por difusión para procesos industriales exigentes.",
    de: "Verstehen Sie Schichtaufbauten, Porenstabilität und thermische Grenzen vor der Spezifikation diffusionsgebundener Filtermedien für anspruchsvolle Industrieprozesse.",
    fr: "Comprenez l'agencement des couches, la stabilité des pores et les limites thermiques avant de spécifier un milieu filtrant fritté par diffusion pour des procédés industriels exigeants.",
    pt: "Compreenda as configurações de camadas, a estabilidade dos poros e os limites térmicos antes de especificar meios filtrantes sinterizados por difusão para processos industriais exigentes."
  },
  structureHeading: {
    en: "Layer structure & composite layout",
    zh: "层叠结构与复合构造",
    es: "Estructura de capas y diseño compuesto",
    de: "Schichtstruktur & Verbundaufbau",
    fr: "Structure multicouche et agencement composite",
    pt: "Estrutura de camadas e disposição composta"
  },
  structureSub: {
    en: "How diffusion-bonding stacks determine strength and flow rates.",
    zh: "扩散焊接叠层方式如何决定机械强度与流体通量。",
    es: "Cómo la disposición de capas unidas por difusión define la resistencia mecánica y el caudal.",
    de: "Wie der Aufbau diffusionsgebundener Schichten Festigkeit und Durchfluss bestimmt.",
    fr: "Comment l'empilement lié par diffusion détermine la résistance mécanique et le débit.",
    pt: "Como a sobreposição por difusão determina a resistência mecânica e as taxas de vazão."
  },
  thicknessRangeLabel: {
    en: "Thickness/Range: ",
    zh: "厚度/范围: ",
    es: "Espesor/Rango: ",
    de: "Dicke/Bereich: ",
    fr: "Épaisseur/Plage : ",
    pt: "Espessura/Faixa: "
  },
  applicationsLabel: {
    en: "Applications: ",
    zh: "应用场景: ",
    es: "Aplicaciones: ",
    de: "Anwendungen: ",
    fr: "Applications : ",
    pt: "Aplicações: "
  },
  comparisonTitle: {
    en: "Sintered vs standard woven mesh",
    zh: "烧结网与标准编织网技术对比",
    es: "Malla sinterizada frente a malla tejida estándar",
    de: "Sintergewebe im Vergleich zu Standard-Drahtgewebe",
    fr: "Toile frittée vs toile tissée standard",
    pt: "Malha sinterizada vs malha tecida padrão"
  },
  featureHeader: {
    en: "Feature",
    zh: "特性参数",
    es: "Característica",
    de: "Merkmal",
    fr: "Caractéristique",
    pt: "Característica"
  },
  sinteredHeader: {
    en: "Sintered",
    zh: "烧结丝网",
    es: "Sinterizada",
    de: "Gesintert",
    fr: "Frittée",
    pt: "Sinterizada"
  },
  wovenHeader: {
    en: "Woven",
    zh: "普通编织网",
    es: "Tejida",
    de: "Drahtgewebe",
    fr: "Tissée",
    pt: "Tecida"
  },
  navBoxTitle: {
    en: "Explore product specifications",
    zh: "探索相关产品规格",
    es: "Explorar especificaciones de productos",
    de: "Produktspezifikationen erkunden",
    fr: "Explorer les spécifications produits",
    pt: "Explorar especificações dos produtos"
  },
  navBoxDesc: {
    en: "Review detailed data sheets, micron charts, and stock dimensions for specific sintered configurations.",
    zh: "查阅特定烧结规格的详细数据表、微米过滤图表及现货尺寸。",
    es: "Consulte fichas técnicas detalladas, tablas de micras y dimensiones para configuraciones sinterizadas específicas.",
    de: "Detaillierte Datenblätter, Mikron-Diagramme und Abmessungen für spezifische Sinteraufbauten ansehen.",
    fr: "Consultez les fiches techniques, tableaux de filtration et dimensions disponibles pour les toiles frittées.",
    pt: "Consulte fichas técnicas detalhadas, tabelas de micragem e dimensões para configurações sinterizadas específicas."
  },
  overviewLink: {
    en: "Sintered wire mesh overview",
    zh: "烧结丝网产品概述",
    es: "Visión general de malla sinterizada",
    de: "Übersicht Sinterdrahtgewebe",
    fr: "Aperçu de la toile métallique frittée",
    pt: "Visão geral da malha de arame sinterizada"
  },
  multilayerLink: {
    en: "Sintered multilayer laminate specs",
    zh: "多层复合烧结网规格参数",
    es: "Especificaciones de laminados multicapa sinterizados",
    de: "Spezifikationen für mehrlagiges Sintergewebe",
    fr: "Spécifications des stratifiés multicouches frittés",
    pt: "Especificações de laminados multicamadas sinterizados"
  },
  consultHeading: {
    en: "Need custom layer stacking or differential pressure calculations?",
    zh: "需要定制层叠结构或压差计算支持？",
    es: "¿Requiere apilamiento de capas a medida o cálculo de caída de presión?",
    de: "Benötigen Sie einen individuellen Schichtaufbau oder Druckverlustberechnungen?",
    fr: "Besoin d'un empilement multicouche sur mesure ou d'un calcul de perte de charge ?",
    pt: "Necessita de empilhamento de camadas sob medida ou cálculo de perda de carga?"
  },
  consultDesc: {
    en: "Send your media requirements, target flow rates, and operating temperatures for engineering review.",
    zh: "提交您的介质过滤要求、目标流量及工作温度，获取专业工程技术评估。",
    es: "Envíe sus requisitos de filtración, caudal de diseño y temperatura operativa para una revisión de ingeniería.",
    de: "Senden Sie uns Prozessmedien, Zieldurchfluss und Betriebstemperaturen für eine ingenieurtechnische Bewertung.",
    fr: "Transmettez la nature de vos fluides, vos débits cibles et températures de service pour une évaluation technique.",
    pt: "Envie seus requisitos de fluido, taxas de vazão e temperaturas operacionais para análise de engenharia."
  },
  consultBtn: {
    en: "Contact engineers",
    zh: "咨询工程师",
    es: "Consultar a ingenieros",
    de: "Ingenieure kontaktieren",
    fr: "Consulter nos ingénieurs",
    pt: "Falar com engenheiros"
  }
};
const technicalPillars: TechnicalPillar[] = [{
  icon: Filter,
  title: {
    en: "Rigid pore geometry",
    zh: "刚性孔隙几何结构",
    es: "Geometría de poro rígida",
    de: "Starre Porengeometrie",
    fr: "Géométrie des pores rigide",
    pt: "Geometria de poros rígida"
  },
  summary: {
    en: "Fixed micron ratings without fiber migration",
    zh: "固定微米级过滤精度，无纤维迁移",
    es: "Micraje fijo sin migración de partículas ni fibras",
    de: "Fixe Filterfeinheit ohne Fasermigration",
    fr: "Seuil de filtration fixe sans migration de fibres",
    pt: "Micragem fixa sem desprendimento de fibras"
  },
  detail: {
    en: "Unlike non-woven media or unbonded mesh, solid-state diffusion bonding fuses every wire intersection. Pore apertures remain permanently fixed under continuous forward flow and reverse pulsation.",
    zh: "与无纺布滤材或未烧结丝网不同，固相扩散焊接将每个金属丝交叉点熔接为一体。在持续正向流动与反向脉冲清洗下，孔径大小始终保持恒定不变。",
    es: "A diferencia de los medios no tejidos o mallas sueltas, la unión por difusión en fase sólida fusiona cada punto de cruce del alambre. Las aberturas permanecen permanentemente fijas bajo flujo continuo e impulsos inversos.",
    de: "Im Gegensatz zu Vliesstoffen oder losem Drahtgewebe verschmilzt die Festkörper-Diffusionsschweißung jeden Drahtkreuzungspunkt. Die Porenöffnungen bleiben bei kontinuierlicher Durchströmung und Rückspülung dauerhaft stabil.",
    fr: "Contrairement aux médias non tissés ou toiles non soudées, le frittage par diffusion à l'état solide fusionne chaque intersection de fil. Les ouvertures restent rigoureusement stables sous flux continu et pulsations de rétrolavage.",
    pt: "Diferente de meios não tecidos ou malhas soltas, a soldagem por difusão em estado sólido funde cada ponto de cruzamento dos arames. As aberturas dos poros permanecem estáveis sob fluxo contínuo e pulsos de retrolavagem."
  }
}, {
  icon: ShieldCheck,
  title: {
    en: "Mechanical strength",
    zh: "机械强度与耐压性能",
    es: "Resistencia mecánica",
    de: "Mechanische Festigkeit",
    fr: "Résistance mécanique",
    pt: "Resistência mecânica"
  },
  summary: {
    en: "Handles high differential pressure (ΔP)",
    zh: "承受高压差（ΔP）负荷",
    es: "Soporta alta presión diferencial (ΔP)",
    de: "Hält hohen Differenzdrücken (ΔP) stand",
    fr: "Supporte de fortes pertes de charge (ΔP)",
    pt: "Suporta altas pressões diferenciais (ΔP)"
  },
  detail: {
    en: "Diffusion bonding gives the mesh high tensile and compressive strength. Sintered sheets withstand bursting pressures and severe backwash shock cycles without media deformation or tearing.",
    zh: "扩散焊接赋予丝网极高的抗拉与抗压强度。烧结网板能够承受爆破压力和剧烈的反冲洗冲击循环，避免滤材变形或撕裂。",
    es: "La unión por difusión confiere a la malla una alta resistencia a tracción y compresión. Las placas sinterizadas resisten presiones de rotura y ciclos bruscos de retrolavado sin deformación ni desgarro.",
    de: "Diffusionsschweißen verleiht dem Drahtgewebe eine hohe Zug- und Druckfestigkeit. Gesinterte Platten widerstehen Berstdrücken und harten Rückspülzyklen ohne Medienverformung oder Rissbildung.",
    fr: "La liaison par diffusion confère une très haute résistance à la traction et à la compression. Les tôles frittées résistent aux pressions d'éclatement et aux cycles de choc de rétrolavage sans déformation ni déchirure.",
    pt: "A união por difusão confere alta resistência à tração e à compressão. As placas sinterizadas suportam pressões de ruptura e choques térmicos/mecânicos de retrolavagem sem deformação do meio filtrante."
  }
}, {
  icon: ThermometerSnowflake,
  title: {
    en: "Thermal & chemical limits",
    zh: "耐温与耐化学腐蚀极限",
    es: "Límites térmicos y químicos",
    de: "Thermische & chemische Grenzen",
    fr: "Limites thermiques et chimiques",
    pt: "Limites térmicos e químicos"
  },
  summary: {
    en: "Stable performance across cryogenic and high-temperature environments",
    zh: "在深冷与高温工况下保持稳定性能",
    es: "Rendimiento estable en condiciones criogénicas y de alta temperatura",
    de: "Stabile Leistung in Tieftemperatur- und Hochtemperaturbereichen",
    fr: "Performances stables en conditions cryogéniques et à haute température",
    pt: "Desempenho estável em ambientes criogênicos e de alta temperatura"
  },
  detail: {
    en: "Fabricated from AISI 316L, 304, or 310S stainless steel. Retains structural integrity in extreme cryogenic and high-temperature furnace or hot-gas environments.",
    zh: "选用 AISI 316L、304 或 310S 优质不锈钢制造。在极端深冷、高温炉腔或热气体环境中保持优异的结构完整性。",
    es: "Fabricado en aceros inoxidables AISI 316L, 304 o 310S. Mantiene la integridad estructural tanto en condiciones criogénicas extremas como en hornos o gases calientes a alta temperatura.",
    de: "Hergestellt aus Edelstahl AISI 316L, 304 oder 310S. Behält seine strukturelle Integrität in kryogenen Umgebungen sowie in Hochtemperaturöfen oder Heißgasströmen bei.",
    fr: "Fabriqué en acier inoxydable AISI 316L, 304 ou 310S. Conserve son intégrité structurelle en conditions cryogéniques extrêmes ainsi que dans les fours et gaz chauds à haute température.",
    pt: "Fabricado em aço inoxidável AISI 316L, 304 ou 310S. Mantém a integridade estrutural em temperaturas criogênicas extremas e em fornos ou correntes de gases a alta temperatura."
  }
}, {
  icon: Cpu,
  title: {
    en: "Workability & cleanability",
    zh: "加工性与可清洗再生性",
    es: "Mecanizado y capacidad de limpieza",
    de: "Verarbeitbarkeit & Regenerierbarkeit",
    fr: "Usinabilité et nettoyabilité",
    pt: "Usinabilidade e capacidade de limpeza"
  },
  summary: {
    en: "Fully weldable, machinable, and reusable",
    zh: "完全可焊接、可机械加工且支持重复使用",
    es: "Totalmente soldable, mecanizable y reutilizable",
    de: "Vollständig schweißbar, bearbeitbar und wiederverwendbar",
    fr: "Entièrement soudable, usinable et réutilisable",
    pt: "Totalmente soldável, usinável e reutilizável"
  },
  detail: {
    en: "Sintered mesh sheets can be sheared, folded, rolled into cylinders, and TIG/laser welded without aperture distortion. Cleanable via ultrasonic bath, chemical solvent flushing, or pyrolysis.",
    zh: "烧结网板可剪切、折弯、卷圆成筒，并进行 TIG/激光焊接而不造成孔径失真。支持超声波清洗、化学溶剂冲洗或高温热解再生。",
    es: "Las láminas de malla sinterizada se pueden cizallar, plegar, conformar en cilindros y soldar mediante TIG o láser sin distorsionar la apertura. Se limpian mediante ultrasonidos, disolventes o pirólisis.",
    de: "Gesinterte Gewebebleche können geschert, gekantet, zu Zylindern gerollt und mittels WIG/Laser geschweißt werden, ohne dass die Porengeometrie verzerrt wird. Regenerierbar per Ultraschallbad, chemischer Spülung oder Pyrolyse.",
    fr: "Les feuilles frittées peuvent être cisaillées, pliées, roulées en cylindres et soudées par TIG ou laser sans déformation des ouvertures. Nettoyage possible par ultrasons, solvants ou pyrolyse.",
    pt: "Placas de malha sinterizada podem ser cortadas, dobradas, calandradas em cilindros e soldadas via TIG/laser sem distorção das aberturas. Limpeza viável por banho ultrassônico, solventes químicos ou pirólise."
  }
}];
const layerConfigurations: LayerConfig[] = [{
  title: {
    en: "1-Layer / 2-Layer Composite",
    zh: "单层 / 双层复合烧结网",
    es: "Compuesto de 1 / 2 capas",
    de: "1-lagiger / 2-lagiger Verbund",
    fr: "Composite 1 couche / 2 couches",
    pt: "Composto de 1 / 2 camadas"
  },
  tag: {
    en: "Standard & Laminate",
    zh: "标准与复合型",
    es: "Estándar y laminado",
    de: "Standard & Laminat",
    fr: "Standard & stratifié",
    pt: "Padrão e laminado"
  },
  desc: {
    en: "Single fine woven wire cloth laminated with a coarse square support mesh. Ideal for light-to-moderate differential pressure backwash filters.",
    zh: "单层精细编织丝网与粗目方孔支撑网复合。适用于轻度至中等压差的反冲洗过滤器。",
    es: "Malla de alambre fino laminada con una malla de soporte de alambre grueso. Ideal para filtros de retrolavado con presión diferencial baja a moderada.",
    de: "Feines Drahtgewebe laminiert mit grobem Vierkant-Stützgewebe. Ideal für Rückspülfilter bei leichten bis mittleren Differenzdrücken.",
    fr: "Toile tissée fine laminée avec une toile de soutien carrée plus grossière. Idéale pour les filtres à rétrolavage à perte de charge faible à modérée.",
    pt: "Malha fina de arame laminada com uma malha suporte quadrada mais grossa. Ideal para filtros de retrolavagem com pressão diferencial leve a moderada."
  },
  specs: {
    en: "0.5mm to 1.2mm nominal thickness",
    zh: "公称厚度 0.5mm 至 1.2mm",
    es: "Espesor nominal de 0,5 mm a 1,2 mm",
    de: "0,5 mm bis 1,2 mm Nenndicke",
    fr: "Épaisseur nominale de 0,5 mm à 1,2 mm",
    pt: "Espessura nominal de 0,5 mm a 1,2 mm"
  },
  useCase: {
    en: "Hydraulic oil, suction strainers, polymer screen packs",
    zh: "液压油、吸入式过滤器、聚合物滤片",
    es: "Aceite hidráulico, filtros de succión, paquetes para polímeros",
    de: "Hydrauliköl, Ansaugfilter, Polymer-Siebpakete",
    fr: "Huile hydraulique, crépines d'aspiration, packs pour polymères",
    pt: "Óleo hidráulico, filtros de sucção, pacotes para polímeros"
  }
}, {
  title: {
    en: "5-Layer Sintered Wire Mesh",
    zh: "五层标准烧结丝网",
    es: "Malla de alambre sinterizada de 5 capas",
    de: "5-lagiges Standard-Sinterdrahtgewebe",
    fr: "Toile métallique frittée 5 couches",
    pt: "Malha de arame sinterizada padrão de 5 camadas"
  },
  tag: {
    en: "Standard Industrial Spec",
    zh: "工业标准规范",
    es: "Norma industrial estándar",
    de: "Industriestandard",
    fr: "Spécification standard",
    pt: "Padrão industrial"
  },
  desc: {
    en: "The global benchmark structure composed of protective layer, control layer, dispersion layer, and two heavy reinforcement layers.",
    zh: "全球通用的基准结构，由保护层、控制层、分散层以及两层重型加强支撑层组合而成。",
    es: "Estructura de referencia global compuesta por capa protectora, capa de control, capa de dispersión y dos capas de refuerzo robustas.",
    de: "Weltweit etablierter Standardaufbau bestehend aus Schutzlage, Filterlage, Verteillage und zwei stabilen Stützlagen.",
    fr: "Structure de référence mondiale composée d'une couche protectrice, d'une couche de filtration, d'une couche de dispersion et de deux couches de renfort.",
    pt: "Estrutura de referência global composta por camada de proteção, camada de controle, camada de dispersão e duas camadas de reforço robustas."
  },
  specs: {
    en: "1.7mm nominal thickness, 1µm to 200µm ratings",
    zh: "公称厚度 1.7mm，精度 1μm 至 200μm",
    es: "Espesor nominal de 1,7 mm, retención de 1 µm a 200 µm",
    de: "1,7 mm Nenndicke, Filterfeinheiten 1 µm bis 200 µm",
    fr: "Épaisseur nominale 1,7 mm, seuil de 1 µm à 200 µm",
    pt: "Espessura nominal de 1,7 mm, micragem de 1 µm a 200 µm"
  },
  useCase: {
    en: "Nutsche filters, fluid bed plates, gas sparging discs",
    zh: "纽氏过滤器、流化床分布板、气体分布鼓泡盘",
    es: "Filtros Nutsche, placas para lecho fluido, discos de barboteo de gas",
    de: "Nutschenfilter, Wirbelschichtteller, Gasbegasungsscheiben",
    fr: "Filtres Nutsche, plaques de lit fluidisé, disques de barbotage",
    pt: "Filtros Nutsche, placas para leito fluidizado, discos de aeração de gás"
  }
}, {
  title: {
    en: "Custom Multi-Layer Laminates",
    zh: "定制多层复合烧结网",
    es: "Laminados multicapa personalizados",
    de: "Kundenspezifische Mehrlagen-Laminate",
    fr: "Stratifiés multicouches sur mesure",
    pt: "Laminados multicamadas personalizados"
  },
  tag: {
    en: "3 to 8+ Layers",
    zh: "3 至 8+ 层结构",
    es: "3 a 8+ capas",
    de: "3 bis 8+ Lagen",
    fr: "3 à 8+ couches",
    pt: "3 a 8+ camadas"
  },
  desc: {
    en: "Engineered combinations incorporating perforated plate backing, woven cloth, and fiber felt for high structural rigidity.",
    zh: "结合冲孔板骨架、编织丝网与金属纤维毡的工程定制组合，提供极高的结构刚性。",
    es: "Combinaciones de ingeniería con chapa perforada de soporte, tela tejida y fieltro metálico para máxima rigidez estructural.",
    de: "Maßgeschneiderte Kombinationen mit Lochblechstütze, Drahtgewebe und Metallfaservlies für höchste Formstabilität.",
    fr: "Assemblages techniques intégrant tôle perforée de soutien, toile tissée et feutre métallique pour une rigidité extrême.",
    pt: "Combinações sob medida incorporando chapa perfurada de apoio, tecido de arame e manta metálica para elevada rigidez."
  },
  specs: {
    en: "Custom thickness up to 6.0mm",
    zh: "定制厚度最高达 6.0mm",
    es: "Espesor personalizado de hasta 6,0 mm",
    de: "Individuelle Dicken bis zu 6,0 mm",
    fr: "Épaisseurs sur mesure jusqu'à 6,0 mm",
    pt: "Espessura sob medida de até 6,0 mm"
  },
  useCase: {
    en: "Centrifuge baskets, high-viscosity resins, catalyst recovery",
    zh: "离心机滤篮、高粘度树脂、催化剂回收",
    es: "Cestas de centrifugado, resinas de alta viscosidad, recuperación de catalizador",
    de: "Zentrifugenkörbe, hochviskose Harze, Katalysatorrückgewinnung",
    fr: "Paniers de centrifugeuse, résines haute viscosité, récupération de catalyseur",
    pt: "Cestos de centrífugas, resinas de alta viscosidade, recuperação de catalisador"
  }
}];
const comparisonMetrics: ComparisonMetric[] = [{
  property: {
    en: "Filtration Rating (Nominal/Absolute)",
    zh: "过滤精度（公称 / 绝对）",
    es: "Grado de filtración (nominal / absoluto)",
    de: "Filterfeinheit (nominal / absolut)",
    fr: "Seuil de filtration (nominal / absolu)",
    pt: "Grau de filtração (nominal / absoluto)"
  },
  sintered: {
    en: "1 μm – 200 μm (Rigid)",
    zh: "1 μm – 200 μm（刚性固定）",
    es: "1 μm – 200 μm (Rígido)",
    de: "1 μm – 200 μm (Formstabil)",
    fr: "1 μm – 200 μm (Rigide)",
    pt: "1 μm – 200 μm (Rígido)"
  },
  unbonded: {
    en: "2 μm – 500 μm (Vulnerable to shifting)",
    zh: "2 μm – 500 μm（易受压位移）",
    es: "2 μm – 500 μm (Vulnerable a desplazamiento)",
    de: "2 μm – 500 μm (Drahtverschiebung möglich)",
    fr: "2 μm – 500 μm (Sensible au déplacement des fils)",
    pt: "2 μm – 500 μm (Sujeito a deslocamento dos fios)"
  }
}, {
  property: {
    en: "Burst / Pressure Resistance",
    zh: "耐爆破 / 耐压差性能",
    es: "Resistencia a rotura / presión diferencial",
    de: "Berst- / Differenzdruckfestigkeit",
    fr: "Résistance à l'éclatement / pression différentielle",
    pt: "Resistência à ruptura / pressão diferencial"
  },
  sintered: {
    en: "Extremely high with support layers",
    zh: "带加强支撑层，强度极高",
    es: "Extremadamente alta con capas de soporte",
    de: "Sehr hoch durch integrierte Stützlagen",
    fr: "Extrêmement élevée grâce aux couches de renfort",
    pt: "Extremamente alta com camadas de reforço"
  },
  unbonded: {
    en: "Requires separate backing plates",
    zh: "需额外配置独立支撑板",
    es: "Requiere placas de respaldo adicionales",
    de: "Erfordert separate Stützplatten",
    fr: "Nécessite des tôles de maintien séparées",
    pt: "Requer placas de apoio independentes"
  }
}, {
  property: {
    en: "Ultrasonic / Backwash Reusability",
    zh: "超声波 / 反冲洗复用性",
    es: "Reutilización tras limpieza ultrasónica / retrolavado",
    de: "Ultraschall- / Rückspülregenerierbarkeit",
    fr: "Réutilisabilité après ultrasons / rétrolavage",
    pt: "Reutilização por ultrassom / retrolavagem"
  },
  sintered: {
    en: "Repeated cycles without loss of rating",
    zh: "可多次循环再生，精度不衰减",
    es: "Ciclos repetidos sin pérdida de micraje",
    de: "Vielfache Zyklen ohne Veränderung der Filterfeinheit",
    fr: "Cycles répétés sans dégradation du seuil",
    pt: "Ciclos repetidos sem perda de retenção nominal"
  },
  unbonded: {
    en: "Limited backwash cycles",
    zh: "反洗耐受次数有限",
    es: "Ciclos de retrolavado limitados",
    de: "Begrenzte Anzahl an Rückspülzyklen",
    fr: "Nombre de cycles de rétrolavage limité",
    pt: "Ciclos de retrolavagem limitados"
  }
}];
export default function MeshGuide() {
  const {
    lang
  } = useLanguage();
  return <section id="sinteredmeshengineeringguide" data-nav="light" className="bg-background text-foreground mf-section border-b border-border">
      <div className="mf-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 bg-muted text-foreground">
            {pick(lang, copyData.sectionBadge)}
          </Badge>
          <h2 className="mf-h2 font-default font-semibold tracking-tight text-foreground text-balance">
            {pick(lang, copyData.heading)}
          </h2>
          <p className="mt-4 mf-lead text-muted-foreground leading-relaxed">
            {pick(lang, copyData.lead)}
          </p>
        </div>

        {/* 4 Pillars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {technicalPillars.map((pillar, i) => {
          const IconComponent = pillar.icon;
          const titleText = pick(lang, pillar.title);
          const summaryText = pick(lang, pillar.summary);
          const detailText = pick(lang, pillar.detail);
          return <Reveal key={i} data-index={i} delay={i * 50}>
                <Card className="h-full border border-border bg-card rounded-lg">
                  <CardHeader className="pb-3">
                    <span className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary mb-3">
                      <IconComponent className="size-5" aria-hidden="true" />
                    </span>
                    <CardTitle className="mf-h3 font-default text-card-foreground">
                      {titleText}
                    </CardTitle>
                    <CardDescription className="text-[13px] font-medium text-primary">
                      {summaryText}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[13px] text-muted-foreground leading-relaxed">
                      {detailText}
                    </p>
                  </CardContent>
                </Card>
              </Reveal>;
        })}
        </div>

        {/* Visual & Layer Architecture Overview */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-16">
          {/* Left: Industrial Structure Breakdown */}
          <div className="lg:col-span-7 space-y-6">
            <div className="border border-border rounded-lg bg-card p-6 md:p-8">
              <div className="flex items-center justify-between gap-4 mb-6">
                <div>
                  <h3 className="mf-h3 font-default font-semibold text-card-foreground">
                    {pick(lang, copyData.structureHeading)}
                  </h3>
                  <p className="text-[13px] text-muted-foreground mt-1">
                    {pick(lang, copyData.structureSub)}
                  </p>
                </div>
                <Layers className="size-6 text-primary shrink-0" />
              </div>

              <div className="space-y-4">
                {layerConfigurations.map((item, index) => {
                const titleStr = pick(lang, item.title);
                const tagStr = pick(lang, item.tag);
                const descStr = pick(lang, item.desc);
                const specsStr = pick(lang, item.specs);
                const useCaseStr = pick(lang, item.useCase);
                return <div key={index} data-index={index} className="p-4 rounded-lg bg-muted/60 border border-border">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <h4 className="font-semibold text-foreground text-base">
                          {titleStr}
                        </h4>
                        <span className="text-[13px] px-2.5 py-0.5 rounded bg-background border border-border text-foreground font-mono">
                          {tagStr}
                        </span>
                      </div>
                      <p className="text-[13px] text-muted-foreground mb-3">
                        {descStr}
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[13px] border-t border-border/80 pt-2.5 text-foreground">
                        <div>
                          <span className="font-medium text-muted-foreground">
                            {pick(lang, copyData.thicknessRangeLabel)}
                          </span>
                          {specsStr}
                        </div>
                        <div>
                          <span className="font-medium text-muted-foreground">
                            {pick(lang, copyData.applicationsLabel)}
                          </span>
                          {useCaseStr}
                        </div>
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>

          {/* Right: Technical Spec Comparison Table & Quick Links */}
          <div className="lg:col-span-5 space-y-6">
            <div className="border border-border rounded-lg bg-card p-6">
              <div className="flex items-center gap-2 text-foreground font-semibold mb-4">
                <FileSpreadsheet className="size-5 text-primary" aria-hidden="true" />
                <span>{pick(lang, copyData.comparisonTitle)}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-[13px] border-collapse">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="py-2.5 px-3 font-semibold text-foreground">
                        {pick(lang, copyData.featureHeader)}
                      </th>
                      <th className="py-2.5 px-3 font-semibold text-primary">
                        {pick(lang, copyData.sinteredHeader)}
                      </th>
                      <th className="py-2.5 px-3 font-semibold text-muted-foreground">
                        {pick(lang, copyData.wovenHeader)}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {comparisonMetrics.map((row, i) => <tr key={i} data-index={i} className="hover:bg-muted/30">
                        <td className="py-2.5 px-3 font-medium text-foreground">{pick(lang, row.property)}</td>
                        <td className="py-2.5 px-3 text-foreground font-mono">{pick(lang, row.sintered)}</td>
                        <td className="py-2.5 px-3 text-muted-foreground">{pick(lang, row.unbonded)}</td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Product navigation links box */}
            <div className="border border-border rounded-lg bg-muted/40 p-6">
              <h4 className="font-semibold text-foreground text-[13px] uppercase tracking-wider mb-2">
                {pick(lang, copyData.navBoxTitle)}
              </h4>
              <p className="text-[13px] text-muted-foreground mb-4">
                {pick(lang, copyData.navBoxDesc)}
              </p>

              <div className="flex flex-col gap-3">
                <Button variant="default" className="justify-between w-full py-3 px-4" asChild>
                  <Link to="/SinteredWireMeshPage">
                    <span className="text-left font-medium">
                      {pick(lang, copyData.overviewLink)}
                    </span>
                    <ArrowRight className="size-4 shrink-0" aria-hidden="true" />
                  </Link>
                </Button>

                <Button variant="outline" className="justify-between w-full py-3 px-4 bg-background" asChild>
                  <Link to="/SinteredMultilayerMeshPage">
                    <span className="text-left font-medium">
                      {pick(lang, copyData.multilayerLink)}
                    </span>
                    <ArrowRight className="size-4 shrink-0" aria-hidden="true" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        <Separator className="my-10" />

        {/* Engineering Consultation Callout */}
        <div className="bg-card border border-border rounded-lg p-6 sm:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-1">
            <h3 className="mf-h3 font-default font-semibold text-card-foreground">
              {pick(lang, copyData.consultHeading)}
            </h3>
            <p className="text-[13px] text-muted-foreground">
              {pick(lang, copyData.consultDesc)}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0 w-full sm:w-auto">
            <Button variant="outline" className="w-full sm:w-auto" asChild>
              <Link to="/ContactPage">{pick(lang, copyData.consultBtn)}</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}